import { PageHeader } from "@bafe/ui";
import { useParams } from "react-router";

import { TabNav } from "../../components/TabNav";
import { isMailOnlyOrganisationRole } from "../../lib/mail-only-member.js";
import { activeMembership, useSession } from "../../lib/session";
import { useOrganisationStore } from "../../lib/stores";
import { WorkspaceNeeded } from "../shell/WorkspacePages";
import { DataSection } from "./DataSection";
import { NotificationsSection } from "./NotificationsSection";
import { OrganisationSection } from "./OrganisationSection";
import { ProfileSection } from "./ProfileSection";
import { SecuritySection } from "./SecuritySection";
import { SessionsSection } from "./SessionsSection";
import { TeamSection } from "./TeamSection";

const SECTIONS = [
	"organisation",
	"profile",
	"team",
	"security",
	"notifications",
	"sessions",
	"data",
] as const;

type SectionId = (typeof SECTIONS)[number];

const SECTION_LABEL: Record<SectionId, string> = {
	organisation: "Organisation",
	profile: "Profile",
	team: "Team",
	security: "Security",
	notifications: "Notifications",
	sessions: "Sessions",
	data: "Data and privacy",
};

function isSectionId(value: string | undefined): value is SectionId {
	return SECTIONS.includes((value ?? "") as SectionId);
}

const WORKSPACE_SECTIONS = new Set<SectionId>(["organisation", "team", "notifications"]);

const MAIL_ONLY_SECTIONS = new Set<SectionId>(["profile", "security", "sessions", "data"]);

export function Settings() {
	const { section } = useParams();
	const sessionQuery = useSession();
	const organisationId = useOrganisationStore((state) => state.organisationId);
	const membership = sessionQuery.data ? activeMembership(sessionQuery.data, organisationId) : null;
	const mailOnly = membership ? isMailOnlyOrganisationRole(membership.role) : false;
	const noWorkspace = Boolean(sessionQuery.data && sessionQuery.data.memberships.length === 0);
	const visible = mailOnly
		? SECTIONS.filter((id) => MAIL_ONLY_SECTIONS.has(id))
		: noWorkspace
			? SECTIONS.filter((id) => !WORKSPACE_SECTIONS.has(id))
			: SECTIONS;
	const requested: SectionId = isSectionId(section) ? section : noWorkspace ? "profile" : "organisation";
	const current = visible.includes(requested) ? requested : (visible[0] ?? "profile");

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title="Settings"
				description={
					noWorkspace
						? "Your account security. Organisation settings belong to a workspace."
						: "Your organisation, your team and your account security."
				}
			/>

			<TabNav
				label="Settings sections"
				currentId={current}
				items={visible.map((id) => ({ id, label: SECTION_LABEL[id], to: `/manage/account/${id}` }))}
			/>

			{noWorkspace && WORKSPACE_SECTIONS.has(requested) ? (
				<WorkspaceNeeded platformOwner={Boolean(sessionQuery.data?.platformOwner)} />
			) : (
				<>
					{current === "organisation" && <OrganisationSection />}
					{current === "profile" && sessionQuery.data && <ProfileSection session={sessionQuery.data} />}
					{current === "team" && sessionQuery.data && <TeamSection session={sessionQuery.data} />}
					{current === "security" && sessionQuery.data && <SecuritySection session={sessionQuery.data} />}
					{current === "notifications" && <NotificationsSection />}
					{current === "sessions" && <SessionsSection />}
					{current === "data" && <DataSection />}
				</>
			)}
		</div>
	);
}
